// 2026-08-26 · Utilidades del árbol de categorías N niveles.
// Trabaja sobre el array plano `state.categories.categories` (cada item
// con `id, name, idParent, order`) y devuelve estructuras optimizadas
// para el picker jerárquico, breadcrumbs y filtrado por subárbol.

const NULL_PARENT = null;

/**
 * Devuelve un array PLANO ordenado DFS con `depth` y `pathLabel` por nodo.
 * Útil para renderizar Autocomplete con indentación visual y opción
 * seleccionable de cualquier nivel del árbol.
 *
 * Ejemplo:
 *   [
 *     { id: 1, name: "Bebidas", depth: 0, pathLabel: "Bebidas" },
 *     { id: 2, name: "Cervezas", depth: 1, pathLabel: "Bebidas › Cervezas" },
 *     { id: 3, name: "Tercios", depth: 2, pathLabel: "Bebidas › Cervezas › Tercios" },
 *     { id: 4, name: "Comida", depth: 0, pathLabel: "Comida" },
 *   ]
 */
export function flattenCategoriesForPicker(categories, { onlyActive = true } = {}) {
  if (!Array.isArray(categories) || categories.length === 0) return [];
  const source = onlyActive
    ? categories.filter((c) => c?.isActive !== false)
    : categories.slice();

  // Índice por padre para lookup O(1).
  const byParent = new Map();
  for (const c of source) {
    const parent = c.idParent ?? NULL_PARENT;
    if (!byParent.has(parent)) byParent.set(parent, []);
    byParent.get(parent).push(c);
  }
  // Orden hermanos por `order` numérico ascendente y fallback por name.
  for (const list of byParent.values()) {
    list.sort((a, b) => {
      const ao = a.order ?? 0;
      const bo = b.order ?? 0;
      if (ao !== bo) return ao - bo;
      return String(a.name || "").localeCompare(String(b.name || ""), "es");
    });
  }

  const out = [];
  const walk = (parentId, depth, pathAccumulator) => {
    const kids = byParent.get(parentId) || [];
    for (const c of kids) {
      const nextPath = [...pathAccumulator, c.name || `#${c.id}`];
      out.push({
        id: c.id,
        name: c.name,
        depth,
        pathLabel: nextPath.join(" › "),
        idParent: c.idParent ?? null,
        raw: c,
      });
      walk(c.id, depth + 1, nextPath);
    }
  };
  walk(NULL_PARENT, 0, []);
  return out;
}

/**
 * Devuelve el path (array de ids) desde raíz hasta `categoryId`.
 * Útil para pintar breadcrumbs y determinar "estoy dentro de X".
 */
export function getCategoryPath(categories, categoryId) {
  if (!categoryId || !Array.isArray(categories)) return [];
  const byId = new Map(categories.map((c) => [c.id, c]));
  const path = [];
  let current = byId.get(categoryId);
  const seen = new Set(); // guard defensivo contra ciclos (no deberían existir)
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    path.unshift(current);
    if (!current.idParent) break;
    current = byId.get(current.idParent);
  }
  return path;
}

/**
 * Devuelve los ids de todos los descendientes de `rootId` (recursivo),
 * incluyendo el propio root. Se usa para filtrar productos por subárbol
 * cuando el camarero se sitúa en una categoría — muestra los productos
 * de ese nodo Y de todos sus hijos.
 */
export function getDescendantIds(categories, rootId) {
  if (!rootId || !Array.isArray(categories)) return new Set([rootId].filter(Boolean));
  const byParent = new Map();
  for (const c of categories) {
    const p = c.idParent ?? NULL_PARENT;
    if (!byParent.has(p)) byParent.set(p, []);
    byParent.get(p).push(c.id);
  }
  const out = new Set([rootId]);
  const queue = [rootId];
  while (queue.length > 0) {
    const currentId = queue.shift();
    const kids = byParent.get(currentId) || [];
    for (const kid of kids) {
      if (!out.has(kid)) {
        out.add(kid);
        queue.push(kid);
      }
    }
  }
  return out;
}

/**
 * Devuelve los IDs de las categorías hijas DIRECTAS de `parentId` (no
 * recursivo). Con `parentId=null` devuelve categorías raíz. Ordenadas
 * por `order`.
 */
export function getDirectChildren(categories, parentId) {
  if (!Array.isArray(categories)) return [];
  const target = parentId ?? NULL_PARENT;
  return categories
    .filter((c) => (c.idParent ?? NULL_PARENT) === target && c.isActive !== false)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}
